import type { CSSProperties, FC, ReactNode } from 'react';
import { Tag } from 'antd';
import type { IconType } from 'react-icons';
import { TbArchive, TbArchiveOff, TbIcons, TbLock, TbLockOpen, TbSparkles, TbWorld } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OurDocsLink from '@/shared/components/tags/OurDocsLink';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import type { SmallTooltipProps } from '@/shared/components/tooltips/SmallTooltip';

import { getWorkspaceResourceStatusLabel } from './configuration';
import type { WorkspaceResourceMutableStatus, WorkspaceResourceStatus } from './configuration';

interface WorkspaceResourceStatusGlyphProps {
    ariaLabel?: string;
    size: string | number;
    status: WorkspaceResourceStatus;
    style?: CSSProperties;
}

const WorkspaceResourceStatusGlyph: FC<WorkspaceResourceStatusGlyphProps> = ({ ariaLabel, size, status, style }) => {
    const accessibilityProps = ariaLabel ? { 'aria-label': ariaLabel, role: 'img' as const } : { 'aria-hidden': true };

    if (status === 'archived') {
        return <TbArchive {...accessibilityProps} size={size} style={style} />;
    }

    if (status === 'locked') {
        return <TbLock {...accessibilityProps} size={size} style={style} />;
    }

    if (status === 'new') {
        return <TbSparkles {...accessibilityProps} size={size} style={style} />;
    }

    if (status === 'public') {
        return <TbWorld {...accessibilityProps} size={size} style={style} />;
    }

    return <TbIcons {...accessibilityProps} size={size} style={style} />;
};

export interface WorkspaceResourceStatusActionIconProps {
    active?: boolean;
    className?: string;
    size?: string | number;
    status: WorkspaceResourceMutableStatus;
    style?: CSSProperties;
}

/** Canonical activate/deactivate icon for resource action menus and buttons. */
export const WorkspaceResourceStatusActionIcon: FC<WorkspaceResourceStatusActionIconProps> = ({
    active = false,
    className,
    size = '1rem',
    status,
    style,
}) => {
    const iconProps = { 'aria-hidden': true, className, size, style };

    if (status === 'archived') {
        return active ? <TbArchiveOff {...iconProps} /> : <TbArchive {...iconProps} />;
    }

    return active ? <TbLockOpen {...iconProps} /> : <TbLock {...iconProps} />;
};

const WorkspaceResourceStatusTooltip: FC<{ status: WorkspaceResourceStatus }> = ({ status }) => {
    if (status === 'new') {
        return <>This item is new.</>;
    }

    if (status === 'template') {
        return (
            <>
                This item is a template. Learn more about templates in <OurDocsLink />.
            </>
        );
    }

    return (
        <>
            This item is {status}. Learn more about {status} items in <OurDocsLink />.
        </>
    );
};

export interface WorkspaceResourceStatusTagProps {
    children: ReactNode;
    color?: string;
    icon?: IconType;
    iconNode?: ReactNode;
    style?: CSSProperties;
}

/** Height-stable status tag shared by virtualized resource rows and other compact resource surfaces. */
export const WorkspaceResourceStatusTag: FC<WorkspaceResourceStatusTagProps> = ({
    children,
    color,
    icon: Icon,
    iconNode,
    style,
}) => (
    <Tag
        color={color}
        style={{
            alignItems: 'center',
            borderColor: 'transparent',
            display: 'inline-flex',
            height: '1.25rem',
            lineHeight: 1,
            marginInlineEnd: 0,
            marginInlineStart: '0.5rem',
            paddingInline: '0.25rem',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            ...style,
        }}
    >
        {iconNode ??
            (Icon ? (
                <Icon aria-hidden='true' size='0.75rem' style={{ flex: '0 0 auto', marginInlineEnd: '0.25rem' }} />
            ) : null)}
        {children}
    </Tag>
);

export interface WorkspaceResourceStatusIndicatorProps {
    iconSize?: string | number;
    placement?: SmallTooltipProps['placement'];
    presentation?: 'icon' | 'tag';
    status: WorkspaceResourceStatus;
    style?: CSSProperties;
    tooltip?: ReactNode | false;
}

/** Canonical icon, label, tooltip, and compact sizing for one workspace-resource state. */
export const WorkspaceResourceStatusIndicator: FC<WorkspaceResourceStatusIndicatorProps> = ({
    iconSize = '1rem',
    placement = 'topLeft',
    presentation = 'icon',
    status,
    style,
    tooltip,
}) => {
    const { token } = useAntDesignTheme();
    const label = getWorkspaceResourceStatusLabel(status);
    const tagColor =
        status === 'new' ? 'green' : status === 'public' ? 'blue' : status === 'template' ? 'gold' : undefined;
    const indicator =
        presentation === 'tag' ? (
            <WorkspaceResourceStatusTag
                color={tagColor}
                iconNode={
                    <WorkspaceResourceStatusGlyph
                        size='0.75rem'
                        status={status}
                        style={{ flex: '0 0 auto', marginInlineEnd: '0.25rem' }}
                    />
                }
                style={style}
            >
                {label}
            </WorkspaceResourceStatusTag>
        ) : (
            <WorkspaceResourceStatusGlyph
                ariaLabel={label}
                size={iconSize}
                status={status}
                style={{
                    color: token.colorTextTertiary,
                    flex: '0 0 auto',
                    marginInlineStart: '0.5rem',
                    verticalAlign: '-0.125rem',
                    ...style,
                }}
            />
        );

    if (tooltip === false) {
        return indicator;
    }

    return (
        <SmallTooltip
            padding='0.25rem 0'
            placement={placement}
            title={tooltip ?? <WorkspaceResourceStatusTooltip status={status} />}
        >
            <span
                data-workspace-resource-status={status}
                style={{ alignItems: 'center', display: 'inline-flex', flex: '0 0 auto', lineHeight: 1 }}
            >
                {indicator}
            </span>
        </SmallTooltip>
    );
};

export interface WorkspaceResourceStatusIndicatorsProps {
    archived?: boolean;
    iconSize?: string | number;
    isNew?: boolean;
    isPublic?: boolean;
    isTemplate?: boolean;
    locked?: boolean;
    style?: CSSProperties;
}

/** Normalized resource states in the platform-standard title order. */
export const WorkspaceResourceStatusIndicators: FC<WorkspaceResourceStatusIndicatorsProps> = ({
    archived = false,
    iconSize,
    isNew = false,
    isPublic = false,
    isTemplate = false,
    locked = false,
    style,
}) => (
    <>
        {isTemplate ? <WorkspaceResourceStatusIndicator presentation='tag' status='template' /> : null}
        {isPublic ? <WorkspaceResourceStatusIndicator presentation='tag' status='public' /> : null}
        {isNew ? <WorkspaceResourceStatusIndicator presentation='tag' status='new' /> : null}
        {locked ? <WorkspaceResourceStatusIndicator iconSize={iconSize} status='locked' style={style} /> : null}
        {archived ? <WorkspaceResourceStatusIndicator iconSize={iconSize} status='archived' style={style} /> : null}
    </>
);
